import { useEffect } from "react";
import { trackEvent } from "../../services/firebase";
import { APPLY_LINK } from "./shared";
import popupImage from "../../assets/home/popup-image.jpg";
import glowTop from "../../assets/home/popup-glow-top.svg";
import glowTop2 from "../../assets/home/popup-glow-top2.svg";
import glowBottom from "../../assets/home/popup-glow-bottom.svg";
import closeIcon from "../../assets/home/popup-close.svg";
import iconPercent from "../../assets/home/popup-icon-percent.svg";
import iconCard from "../../assets/home/popup-icon-card.svg";
import iconUser from "../../assets/home/popup-icon-user.svg";
import iconRupee from "../../assets/home/popup-icon-rupee.svg";

const FEATURES = [
  { title: "Low Interest Rates", desc: "Competitive rates to make borrowing more affordable.", icon: iconPercent },
  { title: "Low Processing Fee", desc: "Know your processing charges upfront, with transparent fee details.", icon: iconCard },
  { title: "Made for Salaried Professionals", desc: "Designed for professionals whose salary is credited directly to their bank account.", icon: iconUser },
  { title: "No Payment Charges", desc: "No extra charges for making your payment.", icon: iconRupee },
];

export default function HomePopup({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    trackEvent("home_popup_view");
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-white/70 backdrop-blur-[2px] p-4"
      style={{ fontFamily: "'Outfit', sans-serif", animation: "fadeIn 0.3s ease-out" }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="home-popup-title"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[848px] max-h-[calc(100vh-32px)] overflow-y-auto overflow-x-hidden bg-[#f5fffa] border border-[#5c168a] rounded-[26.5px] shadow-[0px_11.925px_31.8px_-10.6px_rgba(6,78,59,0.07)] px-4 sm:px-[36px] pt-[19px] pb-[30px]"
      >
        <div className="pointer-events-none absolute left-[-72px] top-[-90px] h-[294px] w-[301px]">
          <div className="absolute inset-[-40.54%_-39.65%]">
            <img alt="" className="block max-w-none size-full" src={glowTop} />
          </div>
        </div>
        <div className="pointer-events-none absolute left-[625px] top-[296px] size-[238.5px]">
          <div className="absolute inset-[-50%]">
            <img alt="" className="block max-w-none size-full" src={glowTop2} />
          </div>
        </div>
        <div className="pointer-events-none absolute left-[671px] top-[305px] size-[278px]">
          <div className="absolute inset-[-52.38%]">
            <img alt="" className="block max-w-none size-full" src={glowBottom} />
          </div>
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 sm:right-[38px] top-4 sm:top-[23px] z-10 size-6 cursor-pointer"
        >
          <img alt="" src={closeIcon} className="size-6" />
        </button>

        <div className="relative flex flex-col items-center text-center px-6 sm:px-0">
          <span className="inline-flex items-center h-[27px] bg-[#ecfdf5] px-4 rounded-full font-black text-[#10b981] text-[10px] sm:text-[12px] tracking-[2px] uppercase whitespace-nowrap">
            The FastRupees Advantage
          </span>
          <h2 id="home-popup-title" className="font-bold text-[#1a1a4b] text-[26px] sm:text-[40px] leading-[1.2] sm:leading-[48px] tracking-[-1px] uppercase">
            The FastRupees Advantage
          </h2>
          <p className="font-medium text-[#6a7282] text-[14px] sm:text-[16px] leading-[24px]">
            Discover the benefits that make your borrowing journey simpler.
          </p>
        </div>

        <div className="relative mt-[31px]">
          <div className="h-[160px] sm:h-[250px] rounded-[22px] overflow-hidden">
            <img src={popupImage} alt="Growth chart being drawn by hand" className="size-full object-cover object-[center_43%]" />
          </div>
          <span className="absolute left-1/2 -translate-x-1/2 top-[-21px] bg-[#f8fafc] border-[1.289px] border-[rgba(91,22,140,0.5)] rounded-full px-[20.6px] py-[10.3px] font-extrabold text-[#5b168c] text-[12px] sm:text-[15.47px] tracking-[1.547px] uppercase whitespace-nowrap">
            “First 5 Days Interest-Free”
          </span>
        </div>

        <div className="relative mt-4 bg-white border-[0.663px] border-[#d7f3e8] rounded-[15.9px] p-[10.34px] drop-shadow-[0px_7.95px_9.275px_rgba(6,78,59,0.06)] grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-[10px]">
          {FEATURES.map((f) => (
            <div key={f.title} className="bg-[#f8fafc] border-[0.663px] border-[#e5e7eb] rounded-[10.6px] p-[9.94px] md:h-[95px]">
              <div className="flex items-start gap-[5.3px]">
                <span className="flex items-center justify-center shrink-0 size-[21.2px] rounded-full bg-[#f8eeff]">
                  <img alt="" src={f.icon} className="size-3" />
                </span>
                <span className="pt-[5px] font-extrabold text-[#5b168c] text-[9.275px] tracking-[0.3313px] uppercase leading-[1.2]">
                  {f.title}
                </span>
              </div>
              <p className="pl-[26.5px] text-[#6a7282] text-[10px] leading-[16px]">{f.desc}</p>
            </div>
          ))}
        </div>

        <a
          href={APPLY_LINK}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            trackEvent("apply_now_click", { location: "home_popup" });
            onClose();
          }}
          className="relative mt-5 flex items-center justify-center w-full bg-[#5c168a] px-6 py-3 rounded-[45px] font-bold text-[14px] text-white uppercase transition-opacity hover:opacity-90"
          style={{ fontFamily: "'Geist', sans-serif" }}
        >
          Apply Now
        </a>
      </div>
    </div>
  );
}
